import { useCallback } from 'react';

import useDeferForHydrationMismatch from '@core/hooks/useDeferForHydrationMismatch';
import useReadmeApi from '@core/hooks/useReadmeApi';
import { useSuperHubStore } from '@core/store';

interface SectionContentStatus {
  changelog: boolean;
  custom_pages: boolean;
  guides: boolean;
  recipes: boolean;
  reference: boolean;
}

/**
 * Fetches content status for all hub sections. Returns a map of section keys to
 * booleans indicating whether each section has any pages. Used by the nav to
 * hide empty sections from non-admin viewers.
 */
export default function useSectionContentStatus() {
  const [getApiBaseUrl, isSuperHub] = useSuperHubStore(s => [s.getApiBaseUrl, s.isSuperHub]);

  const apiUrl = isSuperHub ? '/sidebar/status' : null;

  const {
    data: response,
    isLoading,
    mutate,
  } = useReadmeApi<{ data: SectionContentStatus }>(apiUrl, {
    apiBaseUrl: getApiBaseUrl(),
    swr: {
      revalidateOnFocus: false,
      shouldRetryOnError: true,
    },
  });

  // Defer the content status to avoid hydration mismatch — SSR renders all
  // nav sections, and we only hide empty ones after the client-side API call.
  const deferredData = useDeferForHydrationMismatch(response?.data ?? null, null);

  // Wrap mutate to unwrap the { data } envelope so callers get SectionContentStatus directly.
  const revalidate = useCallback(async () => {
    const result = await mutate();
    return result?.data ?? null;
  }, [mutate]);

  /**
   * Returns true when a section is known to have no content. Returns false
   * while loading or during SSR so callers can default to showing the section.
   */
  const isSectionEmpty = useCallback(
    (sectionKey: keyof SectionContentStatus) => deferredData?.[sectionKey] === false,
    [deferredData],
  );

  return {
    /** Map of section keys to whether they have content. Null while loading or during SSR. */
    sectionContentStatus: deferredData,
    /** Returns true when a section is known to have no content. */
    isSectionEmpty,
    /** Revalidate the section content status from the API. Returns unwrapped SectionContentStatus. */
    revalidate,
    isLoading,
  };
}
